import type {
  TInviteApplicantStatus,
  TInviteFilterQuery,
  TInviteSortColumn,
  TInviteStaffRole,
} from '@/services/invites';
import type { TApplicantChipVariant } from '@/constants/applicants';

import { APPLICANT_ROLE_LABEL, APPLIED_RANGE } from '@/constants/applicants';
import { DEFAULT_PER_PAGE } from '@/services/api';

import type { TSegmentedOption } from 'components/ui/SegmentedToggle';
import type { TSelectOption } from 'components/ui/Select';

type TInviteTileKey = 'total' | 'open' | 'submitted' | 'approved';

type TInviteTile = {
  key: TInviteTileKey;
  label: string;
  note: string;
  query: TInviteFilterQuery;
};

type TInviteStatusKey = 'generated' | 'opened' | 'in_progress' | 'submitted' | 'approved' | 'expired' | 'revoked';

type TInviteStatusSpec = {
  label: string;
  variant: TApplicantChipVariant;
  query: TInviteFilterQuery;
};

type TInviterFilterKey = 'members' | 'ambassadors' | 'recruiters' | 'concierges' | 'admins';

type TInviterFilterSpec = {
  label: string;
  query: TInviteFilterQuery;
};

type TInviteColumnKey = 'invitation' | 'inviter' | 'applicant' | 'status' | 'generated' | 'lastActivity' | 'expires' | 'actions';

type TInviteColumn = {
  key: TInviteColumnKey;
  label: string;
  width: string;
  sortColumn?: TInviteSortColumn;
};

const INVITES_COPY = {
  title: 'Invitations',
  subtitle: 'Single-use links — who issued them and how far they got.',
  blank: '—',
  searchPlaceholder: 'Search inviter or invitation ID',
  statusPlaceholder: 'All statuses',
  typePlaceholder: 'All types',
  inviterPlaceholder: 'All inviters',
  datePlaceholder: 'Any date',
  memberLabel: 'Member',
  ambassadorLabel: 'Ambassador',
  countOne: 'invitation',
  countMany: 'invitations',
  emptyTitle: 'No invitations match',
  emptyDescription: 'Try a different search or clear the filters.',
  errorDescription: 'The register could not be loaded.',
  details: 'Details',
} as const;

const INVITE_DETAIL_COPY = {
  blank: '—',
  generatedPrefix: 'Generated',
  revoke: 'Revoke',
  newLink: 'New link',
  revokeConfirmTitle: 'Revoke this invitation?',
  revokeConfirmDescription: 'The link stops working at once. A revoked invitation does not return to the inviter.',
  revokeConfirm: 'Revoke',
  cancel: 'Cancel',
  linkTitle: 'Link',
  linkDescription: 'The link is shown once — when it is generated or re-issued. It is not stored, so it cannot be copied again from here. '
    + 'For a team invitation, New link issues a replacement.',
  linkFootnote: 'Single use. The token is the only identifier that travels — it is never the invitation id.',
  inviterTitle: 'Inviter',
  nameLabel: 'Name',
  typeLabel: 'Type',
  applicantTypeLabel: 'Applicant type',
  remainingLabel: 'Invitations remaining',
  unlimited: 'Unlimited',
  applicationTitle: 'Linked application',
  noApplication: 'No application yet.',
  statusTitle: 'Status',
  generatedStep: 'Generated',
  openedStep: 'Opened',
  startedStep: 'Application started',
  submittedStep: 'Application submitted',
  revokedStep: 'Revoked',
  notYet: 'Not yet',
  deliveryNote: 'Delivery is not observable. The link leaves on the member\'s own device — the SMS composer on mobile, the clipboard on desktop — '
    + 'so we cannot tell a link that was never sent from one that was sent and ignored. There is no sent or delivered step here by design.',
  expiryTitle: 'Expiry',
  expiresPrefix: 'Expires',
  expiredPrefix: 'Expired',
  expiryNote: 'Derived from the invitation policy, so changing the expiry setting moves this date.',
  usedExpiry: 'In use — the expiry no longer applies.',
  revokedExpiry: 'Revoked — the link no longer works.',
  errorDescription: 'The invitation could not be loaded.',
  notFoundTitle: 'Invitation not found.',
  notFoundDescription: 'The invitation does not exist, or it is outside what your role can read.',
  back: 'Back to invitations',
} as const;

const INVITATION_REQUESTS_COPY = {
  title: 'Invitation requests',
  subtitle: 'Requests from the public form. Passing is silent — no reply goes out.',
  pendingSuffix: 'pending',
  pass: 'Pass',
  generate: 'Generate invitation',
  empty: 'No pending requests.',
  error: 'The requests could not be loaded.',
  separator: ' · ',
  socialPrefix: '@',
} as const;

const REQUEST_ERROR_COPY: Record<string, string> = {
  '42501': 'Your role cannot answer invitation requests.',
  '22023': 'Pick Single or Client for the invitation.',
  'P0002': 'This request no longer exists.',
  'already_answered': 'This request has already been answered.',
  'not_configured': 'Invitation links are not configured in this environment.',
};

const INVITE_ACTION_ERROR_COPY: Record<string, string> = {
  '42501': 'Your role cannot do this.',
  'P0002': 'This invitation no longer exists.',
  'member_invite': 'A member\'s invitation can only be re-issued by the member, in the app.',
  'consumed': 'This invitation has already been used.',
  'revoked': 'This invitation has been revoked.',
  'not_configured': 'Invitation links are not configured in this environment.',
};

const REISSUE_COPY = {
  title: 'New link',
  description: 'A new link replaces the current one at once — the old link stops working. Copy the new one now: it is shown only once.',
  confirm: 'Issue new link',
  cancel: 'Cancel',
  done: 'Done',
  linkLabel: 'New invitation link',
  linkHint: 'Copy it now — it is not stored and will not be shown again.',
  copy: 'Copy link',
  copied: 'Link copied',
} as const;

const APP_INVITE_REVOKE_ACTION = 'app_invite_revoke';

const ISSUE_ERROR_COPY: Record<string, string> = {
  '42501': 'Your role cannot generate invitations.',
  '22023': 'Pick Single or Client for the invitation.',
  'not_configured': 'Invitation links are not configured in this environment.',
  'already_answered': 'This request has already been answered.',
};

const GENERATE_INVITE_COPY = {
  title: 'Generate invitation',
  description: 'The link opens the application form for the type you pick. Send it to the person yourself, from your work mailbox — nothing is sent by the system.',
  requestDescription: 'The link opens the application form for the type you pick, and it is emailed to the person who asked for it.',
  forLabel: 'For',
  noteLabel: 'Internal note (optional)',
  typeLabel: 'Applicant type',
  typeHint: 'Decides which application form the link opens.',
  generate: 'Generate invitation',
  cancel: 'Cancel',
  done: 'Done',
  linkLabel: 'Invitation link',
  linkHint: 'Copy it now — it is not stored and will not be shown again.',
  copy: 'Copy link',
  copied: 'Link copied',
  emailSending: 'Emailing the link…',
  emailedTo: 'Emailed to',
  emailNotSent: 'The email was not sent — copy the link and send it yourself, or try again.',
  retryEmail: 'Retry email',
  noCalendar: 'No active recruiter or concierge has a Calendly calendar linked, so the applicant would get no interview slots. '
    + 'A super admin links one in Admin Manage.',
} as const;

const REQUEST_INVITEE_OPTIONS: readonly TSegmentedOption[] = [
  { value: 'single', label: 'Single' },
  { value: 'client', label: 'Client' },
];

const APP_INVITE_ISSUE_ACTION = 'app_invite_issue';

const APPROVED_APPLICANT_STATUSES: readonly TInviteApplicantStatus[] = ['activation_ready', 'active'];

const INVITE_TILES: readonly TInviteTile[] = [
  {
    key: 'total',
    label: 'Total generated',
    note: 'Links that exist',
    query: {},
  },
  {
    key: 'open',
    label: 'Currently open',
    note: 'Generated, opened or in progress',
    query: { stages: ['generated', 'opened', 'in_progress'], isExpired: false },
  },
  {
    key: 'submitted',
    label: 'Submitted',
    note: 'An application came back',
    query: { stages: ['submitted'] },
  },
  {
    key: 'approved',
    label: 'Approved',
    note: 'Through to activation or on the app',
    query: { applicantStatuses: APPROVED_APPLICANT_STATUSES },
  },
];

const INVITE_STATUS_KEYS: readonly TInviteStatusKey[] = ['generated', 'opened', 'in_progress', 'submitted', 'approved', 'expired', 'revoked'];

const INVITE_STATUS: Record<TInviteStatusKey, TInviteStatusSpec> = {
  generated: { label: 'Generated', variant: 'neutral', query: { stages: ['generated'], isExpired: false } },
  opened: { label: 'Opened', variant: 'info', query: { stages: ['opened'], isExpired: false } },
  in_progress: { label: 'In progress', variant: 'warning', query: { stages: ['in_progress'] } },
  submitted: {
    label: 'Submitted',
    variant: 'single-pink',
    query: { stages: ['submitted'], excludedApplicantStatuses: APPROVED_APPLICANT_STATUSES },
  },
  approved: {
    label: 'Approved',
    variant: 'success',
    query: { stages: ['submitted'], applicantStatuses: APPROVED_APPLICANT_STATUSES },
  },
  expired: { label: 'Expired', variant: 'zero', query: { stages: ['generated', 'opened'], isExpired: true } },
  revoked: { label: 'Revoked', variant: 'danger', query: { stages: ['revoked'] } },
};

const INVITER_FILTER_KEYS: readonly TInviterFilterKey[] = ['members', 'ambassadors', 'recruiters', 'concierges', 'admins'];

const INVITER_FILTER: Record<TInviterFilterKey, TInviterFilterSpec> = {
  members: { label: 'Members', query: { inviterRoles: ['single', 'client'], isInviterAmbassador: false } },
  ambassadors: { label: 'Ambassadors', query: { isInviterAmbassador: true } },
  recruiters: { label: 'Recruiters', query: { inviterRoles: ['staff'], inviterSubRoles: ['recruiter', 'recruiter_manager'] } },
  concierges: { label: 'Concierges', query: { inviterRoles: ['staff'], inviterSubRoles: ['concierge', 'concierge_manager'] } },
  admins: { label: 'Admins', query: { inviterRoles: ['staff'], inviterSubRoles: ['super_admin'] } },
};

const INVITER_STAFF_LABEL: Record<TInviteStaffRole, string> = {
  recruiter: 'Recruiter',
  recruiter_manager: 'Recruiter Manager',
  concierge: 'Matchmaker (Concierge)',
  concierge_manager: 'Concierge Manager',
  super_admin: 'Super Admin',
};

const INVITE_STATUS_OPTIONS: readonly TSelectOption[] = [
  { value: '', label: INVITES_COPY.statusPlaceholder },
  ...INVITE_STATUS_KEYS.map(key => ({ value: key, label: INVITE_STATUS[key].label })),
];

const INVITEE_TYPES = ['single', 'client'] as const;

const INVITE_TYPE_OPTIONS: readonly TSelectOption[] = [
  { value: '', label: INVITES_COPY.typePlaceholder },
  ...INVITEE_TYPES.map(type => ({ value: type, label: APPLICANT_ROLE_LABEL[type] })),
];

const INVITER_OPTIONS: readonly TSelectOption[] = [
  { value: '', label: INVITES_COPY.inviterPlaceholder },
  ...INVITER_FILTER_KEYS.map(key => ({ value: key, label: INVITER_FILTER[key].label })),
];

const INVITE_FILTER = {
  status: 'status',
  type: 'type',
  inviter: 'inviter',
} as const;

const INVITE_FILTER_KEYS: readonly string[] = [
  INVITE_FILTER.status,
  INVITE_FILTER.type,
  INVITE_FILTER.inviter,
  APPLIED_RANGE.fromID,
  APPLIED_RANGE.toID,
];

const INVITES_PER_PAGE = DEFAULT_PER_PAGE;

const INVITE_SORT_COLUMNS: readonly TInviteSortColumn[] = ['issued_at', 'last_activity_at', 'expires_at'];

const INVITE_COLUMNS: readonly TInviteColumn[] = [
  { key: 'invitation', label: 'Invitation', width: 'w-[156px]' },
  { key: 'inviter', label: 'Inviter', width: 'w-[252px]' },
  { key: 'applicant', label: 'Applicant', width: 'w-[102px]' },
  { key: 'status', label: 'Status', width: 'w-[142px]' },
  {
    key: 'generated',
    label: 'Generated',
    width: 'w-[112px]',
    sortColumn: 'issued_at',
  },
  {
    key: 'lastActivity',
    label: 'Last activity',
    width: 'w-[122px]',
    sortColumn: 'last_activity_at',
  },
  {
    key: 'expires',
    label: 'Expires',
    width: 'w-[136px]',
    sortColumn: 'expires_at',
  },
  { key: 'actions', label: '', width: 'w-[80px]' },
];

const INVITE_DATE_WIDTH = 'w-[160px]';

export type {
  TInviteColumn,
  TInviteColumnKey,
  TInviterFilterKey,
  TInviteStatusKey,
  TInviteStatusSpec,
  TInviteTile,
  TInviteTileKey,
};
export {
  APPROVED_APPLICANT_STATUSES,
  INVITE_COLUMNS,
  INVITE_DATE_WIDTH,
  INVITE_FILTER,
  INVITE_FILTER_KEYS,
  INVITE_SORT_COLUMNS,
  APP_INVITE_ISSUE_ACTION,
  APP_INVITE_REVOKE_ACTION,
  GENERATE_INVITE_COPY,
  INVITATION_REQUESTS_COPY,
  INVITE_ACTION_ERROR_COPY,
  INVITE_DETAIL_COPY,
  INVITE_STATUS,
  INVITE_STATUS_KEYS,
  INVITE_STATUS_OPTIONS,
  INVITE_TILES,
  INVITE_TYPE_OPTIONS,
  ISSUE_ERROR_COPY,
  REQUEST_ERROR_COPY,
  REQUEST_INVITEE_OPTIONS,
  REISSUE_COPY,
  INVITEE_TYPES,
  INVITER_FILTER,
  INVITER_FILTER_KEYS,
  INVITER_OPTIONS,
  INVITER_STAFF_LABEL,
  INVITES_COPY,
  INVITES_PER_PAGE,
};
